import { useRef, useState } from 'react';

import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Heading,
  Modal,
  Paragraph,
  Text,
  TextButton,
} from '@zillow/constellation-10';

import { useRentalManagerUserFreemiumWebApiHandler_isTopOfFunnelEnrollmentEligible } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useScreenSize } from '../../../hooks';
import accessUtils from '../../../utils/accessUtils';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../../constants/FreemiumConstants';
import { WIZARD_TITLE_ID } from '../../../constants/WizardConstants';

import PremiumPurchaseModal from '../../Modal/PremiumPurchaseModal';

import { containerStyles, footerStyles, headerStyles, stepperSlotStyles } from './styles';

import {
  emitClickUpgradeToPremiumFromHeaderEvent,
  emitClickUpgradeToPremiumFromModalEvent,
  EVENT_IDS,
} from '../../../analytics/events/modularListing';
import { useAuth } from '../../AuthProvider';
import RestrictedListingBanner from '../../CreateListingPage/RestrictedListingBanner';
import { JumpToBottomButton } from '../../JumpToBottomButton';
import PremiumPaymentGateway from '../../PaymentGateway/PremiumPaymentGateway';
import PremiumTag from '../../PremiumTag';
import TransitionWrapper from '../../TransitionWrapper';

import type React from 'react';
import type { GetUserPropertyDetailsResponseWebDto } from '@zg-rentals/ts-rental-manager-api-client';

export type PageWizardProps = {
  children: React.ReactNode;
  description?: React.ReactNode;
  isTransitioning?: boolean;
  wizardTitle?: React.ReactNode;
  subtitle?: React.ReactNode;
  title?: string;
  progressStepper: React.ReactNode;
  totalSteps?: number | null;
  currentStepIndex?: number;
  isBackDisabled?: boolean;
  isNextDisabled?: boolean;
  isNextLoading?: boolean;
  exitCTALabel?: string;
  hideDesktopWizardTitle?: boolean;
  hideExitCTA?: boolean;
  nextLabel?: string;
  backLabel?: string;
  onBackClick?: () => void;
  onNextClick?: () => void;
  onNextDataEventId?: string;
  onExitTriggered?: (...args: Array<unknown>) => void;
  hideBackButton?: boolean;
  hideNextButton?: boolean;
  hideJumpToBottomButton?: boolean;
  hideHeader?: boolean;
  hideFooter?: boolean;
  footerContent?: React.ReactNode;
  displayPremiumTopOfFunnelHeader?: boolean;
  footer?: React.ReactNode;
  listingDetails?: GetUserPropertyDetailsResponseWebDto;
};

const FOOTER_BUTTON_MIN_WIDTH = '164px';

const PageWizard = ({
  children,
  description = '',
  isTransitioning = false,
  wizardTitle,
  subtitle = null,
  title = '',
  progressStepper,
  isBackDisabled = false,
  isNextDisabled = false,
  isNextLoading = false,
  nextLabel = 'Next',
  backLabel = 'Back',
  exitCTALabel = 'Exit',
  hideDesktopWizardTitle = false,
  hideExitCTA = false,
  onBackClick = () => {},
  onNextClick = () => {},
  onNextDataEventId,
  onExitTriggered = () => {},
  hideBackButton = false,
  hideNextButton = false,
  hideJumpToBottomButton = false,
  hideHeader = false,
  hideFooter = false,
  footerContent = null,
  displayPremiumTopOfFunnelHeader = false,
  footer = null,
  listingDetails,
}: PageWizardProps) => {
  const { user } = useAuth();
  const { isScreenMobile } = useScreenSize();

  const bodyRef = useRef<HTMLDivElement>(null);

  const isPremiumListing = listingDetails?.freemium?.activeProduct?.type === PRODUCT_TYPE_NAME_TO_ID.PREMIUM;

  const { data: isTopOfFunnelEnrollmentEligible = false } =
    useRentalManagerUserFreemiumWebApiHandler_isTopOfFunnelEnrollmentEligible({
      enabled: listingDetails?.freemium?.isEnabled ?? false,
      meta: { tags: ['isTopOfFunnelEnrollmentEligible'], context: { api: 'freemium', details: 'ListingPage' } },
    });

  const canAccessPremiumUpsell = accessUtils.canAccessTopOfFunnelPremium({
    isFreemiumEnabled: listingDetails?.freemium?.isEnabled ?? false,
    isTopOfFunnelEnrollmentEligible: isTopOfFunnelEnrollmentEligible ?? false,
    isActiveProductTypePremium: isPremiumListing,
  });

  const [displayPremiumPurchaseModal, setDisplayPremiumPurchaseModal] = useState(false);
  const [displayPremiumPaymentGateway, setDisplayPremiumPaymentGateway] = useState(false);

  const showPremiumHeader = canAccessPremiumUpsell && displayPremiumTopOfFunnelHeader;
  const showWizardTitle = !hideDesktopWizardTitle && !(showPremiumHeader && isScreenMobile && isPremiumListing);
  const showUpgradeToPremium = showPremiumHeader && wizardTitle !== 'Publish' && !isPremiumListing;
  const hasVisibleWizardTitle = !hideHeader && showWizardTitle;

  const handleDisplayPremiumPurchaseModal = () => {
    setDisplayPremiumPurchaseModal(true);
    if (listingDetails && user) {
      emitClickUpgradeToPremiumFromHeaderEvent({
        currentProperty: listingDetails,
        user: user,
      });
    }
  };

  const handleSelectPremiumUpgrade = () => {
    setDisplayPremiumPurchaseModal(false);
    setDisplayPremiumPaymentGateway(true);
    if (listingDetails && user) {
      emitClickUpgradeToPremiumFromModalEvent({
        currentProperty: listingDetails,
        user: user,
      });
    }
  };

  const renderHeader = () => (
    <>
      <Modal.Header css={headerStyles}>
        <Box css={{ display: { base: 'inline', md: 'flex' }, alignItems: 'baseline', gap: 'tight', minWidth: 'none' }}>
          {showPremiumHeader && isPremiumListing && <PremiumTag />}
          {showWizardTitle && (
            <Heading id={WIZARD_TITLE_ID} level={1} textStyle="heading-xs" data-testid="desktop-wizard-title">
              {wizardTitle}
            </Heading>
          )}
        </Box>

        <Box css={{ display: 'flex', alignItems: 'center', gap: 'loose', marginInlineStart: 'auto' }}>
          {showUpgradeToPremium && (
            <TextButton
              onClick={handleDisplayPremiumPurchaseModal}
              css={{ display: { base: 'none', lg: 'inherit' } }}
              data-eventid={EVENT_IDS.CLICK_UPGRADE_TO_PREMIUM_FROM_HEADER}
            >
              Upgrade listing to premium
            </TextButton>
          )}
          {!hideExitCTA && (
            <Button
              tone="neutral"
              emphasis="outlined"
              type="button"
              onClick={onExitTriggered}
              data-testid="desktop-exit-button"
              css={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              {exitCTALabel}
            </Button>
          )}
        </Box>
      </Modal.Header>
      <Divider />
    </>
  );

  const renderContent = () => (
    <>
      {title && (
        <Paragraph
          data-testid="title"
          css={{
            textStyle: { base: 'heading-xs', lg: 'heading-md' },
            marginBlockEnd: 'default',
          }}
        >
          {title}
        </Paragraph>
      )}
      {subtitle && (
        <Paragraph textStyle="body" data-testid="subtitle" css={{ marginBlockEnd: 'default' }}>
          {subtitle}
        </Paragraph>
      )}
      {description && (
        <Paragraph textStyle="body" data-testid="description">
          {description}
        </Paragraph>
      )}
      {children}
    </>
  );

  const backButton = (
    <Button
      data-testid="back-button"
      disabled={isBackDisabled}
      onClick={onBackClick}
      type="button"
      tone="neutral"
      emphasis="outlined"
      fluid={{ base: true, lg: false }}
      css={{
        flexGrow: { base: 1, lg: 0 },
        flexBasis: { base: 0, lg: 'auto' },
        minWidth: { lg: FOOTER_BUTTON_MIN_WIDTH },
      }}
    >
      {backLabel}
    </Button>
  );

  const nextButton = (
    <Button
      tone="brand"
      emphasis="filled"
      onClick={onNextClick}
      disabled={isNextDisabled}
      data-testid="next-button"
      type="submit"
      loading={isNextLoading}
      data-eventid={onNextDataEventId}
      fluid={{ base: true, lg: false }}
      css={{
        flexGrow: { base: 1, lg: 0 },
        flexBasis: { base: 0, lg: 'auto' },
        minWidth: { lg: FOOTER_BUTTON_MIN_WIDTH },
      }}
    >
      {nextLabel}
    </Button>
  );

  /*
   * With footerContent the row is Back, status, Next from lg, as it was before. Below lg the status
   * wraps onto a full-width first line and the two buttons share the second, so the buttons keep the
   * equal-width treatment ButtonGroup gives them everywhere else. A ButtonGroup cannot hold the
   * status between its buttons, so this case lays the row out directly.
   */
  const renderFooterWithContent = () => (
    <Box css={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'default' }}>
      {!hideBackButton && backButton}
      <Box
        css={{
          order: { base: -1, lg: 0 },
          flexBasis: { base: '100%', lg: 'auto' },
          flexGrow: { lg: 1 },
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        {footerContent}
      </Box>
      {!hideNextButton && nextButton}
    </Box>
  );

  const renderDefaultFooter = () => (
    <Box data-testid="PageWizard-Footer" css={containerStyles}>
      {footerContent ? (
        renderFooterWithContent()
      ) : (
        <ButtonGroup
          aria-label="Wizard navigation"
          fluid={{ base: true, lg: false }}
          css={{ justifyContent: hideBackButton ? 'flex-end' : 'space-between' }}
        >
          {!hideBackButton && backButton}
          {!hideNextButton && nextButton}
        </ButtonGroup>
      )}
    </Box>
  );

  return (
    <>
      <RestrictedListingBanner />
      {hideHeader ? <Modal.Header css={{ display: 'none' }}>{null}</Modal.Header> : renderHeader()}
      <Modal.Body ref={bodyRef} css={{ paddingInline: 'none', paddingBlock: 'layout.default', position: 'relative' }}>
        <Box css={containerStyles}>
          {!hasVisibleWizardTitle && (
            <Text css={{ srOnly: true }} asChild>
              <span id={WIZARD_TITLE_ID}>{wizardTitle}</span>
            </Text>
          )}
          {progressStepper && <Box css={stepperSlotStyles}>{progressStepper}</Box>}
          {isTransitioning ? (
            <TransitionWrapper isTransitioning={true} />
          ) : (
            <>
              {renderContent()}
              {!hideJumpToBottomButton && <JumpToBottomButton scrollRootRef={bodyRef} />}
            </>
          )}
        </Box>
      </Modal.Body>
      {!hideFooter && !isTransitioning && (
        <>
          <Divider />
          <Modal.Footer css={footerStyles}>
            {footer ? <Box css={containerStyles}>{footer}</Box> : renderDefaultFooter()}
          </Modal.Footer>
        </>
      )}
      {displayPremiumPurchaseModal && (
        <PremiumPurchaseModal
          onClose={() => setDisplayPremiumPurchaseModal(false)}
          onSelectUpgrade={handleSelectPremiumUpgrade}
          freemiumProducts={listingDetails?.freemium?.products ?? []}
          isInBuildingGroup={listingDetails?.freemium?.isInBuildingGroup ?? false}
        />
      )}
      {displayPremiumPaymentGateway && (
        <PremiumPaymentGateway
          listingAlias={listingDetails?.listingAlias ?? ''}
          isActive={listingDetails?.active ?? false}
          isInBuildingGroup={listingDetails?.freemium?.isInBuildingGroup ?? false}
          freemiumData={listingDetails?.freemium ?? undefined}
          isPremiumMidfunnelPayment={true}
          displayContext="NEXT_STEP"
          onClose={() => setDisplayPremiumPaymentGateway(false)}
        />
      )}
    </>
  );
};

export default PageWizard;
